import { useCallback, useEffect, useRef, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { CalendarCheck, Camera, Loader2, Trash2, Sparkles, Images } from "lucide-react";
import { RoomAssign } from "@/components/RoomAssign";
import { toast } from "sonner";

const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");

export function TenancySnapshots({ propertyId }) {
  const [snaps, setSnaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/properties/${propertyId}/snapshots`);
      setSnaps(data);
    } catch (e) { console.error("load snapshots failed", e); }
    finally { setLoading(false); }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try { await api.delete(`/snapshots/${id}`); setSnaps((c) => c.filter((s) => s.snapshot_id !== id)); toast.success("Snapshot removed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remove."); }
  };

  if (loading) return null;

  return (
    <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-5 text-white" data-testid="tenancy-snapshots">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <span className="grid place-items-center h-10 w-10 rounded-xl bg-brand/15 text-brand shrink-0"><CalendarCheck size={20} /></span>
          <div className="min-w-0">
            <h3 className="font-heading font-semibold">Condition snapshots</h3>
            <p className="text-sm text-white/60">Periodic photo check-ins keep your evidence current. Each snapshot is timestamped and locked once saved.</p>
          </div>
        </div>
        <Button size="sm" data-testid="snapshot-take-btn" onClick={() => setDialogOpen(true)}
          className="rounded-xl bg-brand hover:bg-brand-hover text-white shrink-0">
          <Camera size={15} className="mr-1.5" /> Take a snapshot
        </Button>
      </div>

      {snaps.filter((s) => !s.assigned_room).length > 0 && (
        <p className="mt-3 text-[11px] text-white/60 inline-flex items-center gap-1.5" data-testid="snapshots-unassigned-count">
          {snaps.filter((s) => !s.assigned_room).length} checkup{snaps.filter((s) => !s.assigned_room).length === 1 ? "" : "s"} not assigned to a room — assign one below to include it in Condition History.
        </p>
      )}

      {snaps.length === 0 ? (
        <p className="mt-4 text-sm text-white/45" data-testid="snapshots-empty">No snapshots yet — take one every few months to keep a running record.</p>
      ) : (
        <div className="mt-4 space-y-3">
          {snaps.slice().reverse().map((s) => (
            <div key={s.snapshot_id} data-testid={`snapshot-${s.snapshot_id}`} className="rounded-xl border border-white/10 bg-white/5 p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{s.label}</p>
                  <p className="text-[11px] text-white/45 flex items-center gap-2">
                    <span>{fmt(s.created_at)}</span>
                    <span className="inline-flex items-center gap-1"><Images size={11} /> {s.photos?.length || 0} photo{(s.photos?.length || 0) === 1 ? "" : "s"}</span>
                  </p>
                </div>
                <Button size="sm" variant="ghost" data-testid={`snapshot-delete-${s.snapshot_id}`} onClick={() => remove(s.snapshot_id)}
                  className="rounded-lg h-8 w-8 p-0 text-[#FCA5A5] hover:bg-[#7F1D1D]/40 shrink-0"><Trash2 size={14} /></Button>
              </div>
              {s.ai_summary && (
                <p className="mt-1.5 text-xs text-white/70 flex gap-1.5" data-testid={`snapshot-summary-${s.snapshot_id}`}>
                  <Sparkles size={12} className="text-brand shrink-0 mt-0.5" /><span>{s.ai_summary}</span>
                </p>
              )}
              {s.photos?.length > 0 && (
                <div className="mt-2 flex gap-2 overflow-x-auto">
                  {s.photos.map((p) => (
                    <a key={p.photo_id} href={fileUrl(p.storage_path)} target="_blank" rel="noopener noreferrer"
                      className="h-14 w-14 rounded-lg overflow-hidden border border-white/10 shrink-0">
                      <img src={fileUrl(p.storage_path)} alt="snapshot" className="h-full w-full object-cover" />
                    </a>
                  ))}
                </div>
              )}
              <RoomAssign propertyId={propertyId} evidenceType="monthly_checkup" evidenceId={s.snapshot_id}
                assignedRoom={s.assigned_room}
                onChanged={(room) => setSnaps((c) => c.map((x) => x.snapshot_id === s.snapshot_id ? { ...x, assigned_room: room } : x))} />
            </div>
          ))}
        </div>
      )}

      {dialogOpen && <SnapshotDialog propertyId={propertyId} onClose={() => setDialogOpen(false)} onSaved={(s) => { setSnaps((c) => [...c, s]); setDialogOpen(false); }} />}
    </div>
  );
}

function SnapshotDialog({ propertyId, onClose, onSaved }) {
  const [label, setLabel] = useState("Condition check");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const save = async () => {
    if (files.length === 0) { toast.error("Add at least one photo."); return; }
    setSaving(true);
    try {
      const form = new FormData();
      form.append("label", label.trim() || "Condition check");
      files.forEach((f) => form.append("files", f));
      const { data } = await api.post(`/properties/${propertyId}/snapshots`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Snapshot saved");
      onSaved(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save the snapshot.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="snapshot-dialog">
        <DialogHeader>
          <DialogTitle>Take a condition snapshot</DialogTitle>
          <DialogDescription>Add a few photos of the current condition. We'll write a brief summary. Snapshots can't be edited once saved.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Label</Label>
            <Input data-testid="snapshot-label-input" value={label} onChange={(e) => setLabel(e.target.value)} className="mt-1 h-10 rounded-xl" placeholder="e.g. 6-month check" />
          </div>
          <input ref={fileRef} type="file" accept="image/*" multiple capture="environment" className="hidden"
            data-testid="snapshot-file-input" onChange={(e) => setFiles(Array.from(e.target.files || []))} />
          <Button variant="outline" onClick={() => fileRef.current?.click()} className="w-full rounded-xl" data-testid="snapshot-pick-btn">
            <Camera size={16} className="mr-2" /> {files.length > 0 ? `${files.length} photo${files.length === 1 ? "" : "s"} selected` : "Choose photos"}
          </Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl" data-testid="snapshot-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || files.length === 0} className="rounded-xl bg-brand hover:bg-brand-hover text-white" data-testid="snapshot-save">
            {saving ? <><Loader2 size={16} className="animate-spin mr-1.5" /> Saving…</> : "Save snapshot"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
